import { useState } from "react";
import type { CloserData } from "@/hooks/useAttributionData";

function closeRateColor(rate: number): string {
  if (rate >= 50) return "text-emerald-400";
  if (rate >= 30) return "text-yellow-400";
  return "text-red-400";
}

function initials(name: string): string { return name.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase(); }

function CloserExpandedContent({ closer }: { closer: CloserData }) {
  return (
    <div className="border-t border-border p-4" onClick={(e) => e.stopPropagation()}>
      <p className="text-xs font-semibold tracking-wider text-muted-foreground uppercase mb-2">Closed Deals</p>
      <table className="w-full text-xs">
        <thead><tr className="text-muted-foreground border-b border-border">
          <th className="text-left py-1 font-medium">Contact</th>
          <th className="text-left py-1 font-medium">Setter</th>
          <th className="text-left py-1 font-medium">Campaign</th>
          <th className="text-right py-1 font-medium">Deal</th>
        </tr></thead>
        <tbody>{closer.closedDeals.map((deal) => (
          <tr key={deal.id} className="border-b border-border/50 text-muted-foreground">
            <td className="py-1">{deal.contact_name || deal.ghl_contact_id}</td>
            <td className="py-1">{deal.setter_name ?? "—"}</td>
            <td className="py-1 text-blue-400 text-[10px]">{deal.campaign_name ?? "—"}</td>
            <td className="text-right py-1 text-emerald-400">${((deal.deal_value_cents ?? 0) / 100).toLocaleString()}</td>
          </tr>
        ))}</tbody>
      </table>
    </div>
  );
}

interface ClosersTabProps { closers: CloserData[]; loading: boolean; }

export function ClosersTab({ closers, loading }: ClosersTabProps) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const COLS = 3;

  if (loading) return <div className="grid grid-cols-3 gap-4">{Array.from({ length: 3 }).map((_, i) => <div key={i} className="rounded-lg border border-border bg-card p-4 h-40 animate-pulse" />)}</div>;
  if (closers.length === 0) return <div className="flex items-center justify-center h-40 text-muted-foreground text-sm">No closer data for this period.</div>;

  const rows: CloserData[][] = [];
  for (let i = 0; i < closers.length; i += COLS) rows.push(closers.slice(i, i + COLS));
  const expandedCloser = closers.find(c => c.closerId === expandedId) ?? null;
  const expandedRowIdx = expandedId ? Math.floor(closers.findIndex(c => c.closerId === expandedId) / COLS) : -1;

  return (
    <div className="space-y-0">
      {rows.map((row, rowIdx) => (
        <div key={rowIdx}>
          <div className="grid grid-cols-3 gap-4 mb-0">
            {row.map((closer) => {
              const isExpanded = expandedId === closer.closerId;
              return (
                <div key={closer.closerId} className={`rounded-lg border bg-card p-4 cursor-pointer transition-all duration-200 ${isExpanded ? "border-primary/40" : "border-border hover:border-primary/20"}`} onClick={() => setExpandedId(isExpanded ? null : closer.closerId)}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-emerald-400 to-cyan-400 flex items-center justify-center text-xs font-bold text-background shrink-0">{initials(closer.closerName)}</div>
                    <div className="min-w-0"><p className="text-sm font-semibold text-foreground truncate">{closer.closerName}</p><p className="text-[10px] text-muted-foreground">Closer</p></div>
                    <span className="ml-auto text-muted-foreground text-xs">{isExpanded ? "↑" : "↓"}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    <div><p className="text-[9px] font-semibold text-muted-foreground uppercase">Demos Taken</p><p className="text-lg font-bold text-cyan-400">{closer.demosTaken}</p></div>
                    <div><p className="text-[9px] font-semibold text-muted-foreground uppercase">Close Rate</p><p className={`text-lg font-bold ${closeRateColor(closer.closeRate)}`}>{closer.closeRate}%</p></div>
                    <div><p className="text-[9px] font-semibold text-muted-foreground uppercase">Revenue</p><p className="text-lg font-bold text-emerald-400">${Math.round(closer.totalRevenueCents / 100).toLocaleString()}</p></div>
                    <div><p className="text-[9px] font-semibold text-muted-foreground uppercase">Avg Deal</p><p className="text-lg font-bold text-foreground">${Math.round(closer.avgDealCents / 100).toLocaleString()}</p></div>
                  </div>
                  <div className="h-1 rounded-full bg-muted overflow-hidden"><div className="h-full rounded-full bg-gradient-to-r from-cyan-400 to-emerald-400" style={{ width: `${Math.min(closer.closeRate, 100)}%` }} /></div>
                  <p className="text-[10px] text-muted-foreground mt-1">{closer.closeRate}% close rate</p>
                </div>
              );
            })}
          </div>
          {expandedRowIdx === rowIdx && expandedCloser && (
            <div className="rounded-lg border border-primary/30 bg-card/80 mb-4">
              <div className="flex items-center justify-between px-4 pt-4 pb-2">
                <span className="text-sm font-bold text-foreground">{expandedCloser.closerName} — Closed Deals</span>
                <button className="text-xs text-muted-foreground hover:text-foreground" onClick={() => setExpandedId(null)}>↑ collapse</button>
              </div>
              <CloserExpandedContent closer={expandedCloser} />
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
